import React, { useState, useEffect } from 'react';
import { ref, onValue } from "firebase/database";
import { db } from "../firebaseConfig";
import Navbar from '../components/Navbar';
import { 
  IoThermometerOutline, 
  IoWaterOutline, 
  IoStatsChartOutline, 
  IoFlashOutline,
  IoFishOutline 
} from "react-icons/io5";
import { BiCubeAlt } from "react-icons/bi";
import './Dashboard.css';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Registrasi komponen Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [data, setData] = useState({ suhu: 25, ph: 7.92, tds: 600, pakan: 75, beban: 600 });
  const [activeRange, setActiveRange] = useState('Hari Ini');
  const [activeCategory, setActiveCategory] = useState('Semua');

  // State untuk menyimpan riwayat data grafik
  const [chartHistory, setChartHistory] = useState({
    labels: [],
    suhuData: [],
    phData: [],
    tdsData: []
  });

  useEffect(() => {
    const airRef = ref(db, 'monitoring_air');
    onValue(airRef, (snapshot) => {
      const val = snapshot.val();
      if (val) {
        // Kalibrasi pH agar tidak muncul 0.19
        const calibratedPH = val.ph <= 1 ? (val.ph * 41.6).toFixed(2) : val.ph;
        const newData = {
          ...val,
          ph: calibratedPH,
          pakan: val.pakan || 75,
          beban: val.beban || 600
        };
        
        setData(newData);

        // Update riwayat grafik (Menyimpan 10 data terakhir)
        setChartHistory(prev => {
          const newLabels = [...prev.labels, new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })].slice(-10);
          const newSuhu = [...prev.suhuData, newData.suhu].slice(-10);
          const newPH = [...prev.phData, newData.ph].slice(-10);
          const newTDS = [...prev.tdsData, newData.tds].slice(-10);
          
          return { labels: newLabels, suhuData: newSuhu, phData: newPH, tdsData: newTDS };
        });
      }
    });
  }, []);

  // Konfigurasi Data Grafik
  const chartData = {
    labels: chartHistory.labels,
    datasets: [
      {
        label: 'Suhu Air (°C)',
        data: chartHistory.suhuData,
        borderColor: '#f97316',
        backgroundColor: '#f97316',
        tension: 0.4, // Membuat garis melengkung halus
        hidden: activeCategory !== 'Semua' && activeCategory !== 'Suhu Air'
      },
      {
        label: 'pH Air',
        data: chartHistory.phData,
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f6',
        tension: 0.4,
        hidden: activeCategory !== 'Semua' && activeCategory !== 'pH Air'
      },
      {
        label: 'Kualitas Air (PPM)',
        data: chartHistory.tdsData,
        borderColor: '#a855f7',
        backgroundColor: '#a855f7',
        tension: 0.4,
        hidden: activeCategory !== 'Semua' && activeCategory !== 'Kualitas Air'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } }, 
    scales: { 
      x: { grid: { display: false } }, // Sumbu X tanpa garis grid
      y: { beginAtZero: false, border: { dash: [5, 5] } } 
    }
  };

  const pakanInfo = (level) => {
    if (level > 40) return { label: "Aman", class: "baik", msg: "Level pakan masih aman" };
    if (level > 15) return { label: "Peringatan", class: "perhatian", msg: "Pakan mulai menipis!" };
    return { label: "Bahaya", class: "buruk", msg: "Warning: Segera isi pakan!" };
  };

  const currentPakan = pakanInfo(data.pakan);

  return (
    <div className="dashboard-page">
      <Navbar />
      <main className="dashboard-content">
        {/* ROW 1: SENSOR CARDS */}
        <div className="stats-container">
          <div className="metric-card">
            <div className="card-header">
              <span className="card-title"><IoThermometerOutline /> Suhu Air</span>
              <span className={`status-pill ${data.suhu >= 25 && data.suhu <= 30 ? 'baik' : 'buruk'}`}>
                ● {data.suhu >= 25 && data.suhu <= 30 ? 'Baik' : 'Buruk'}
              </span>
            </div>
            <div className="metric-main">
              <span className="big-value">{data.suhu}</span>
              <span className="big-unit">°C</span>
            </div>
            <div className="card-legend">
              <div className="legend-item"><span className="dot baik">●</span> Baik : 25 °C ≤ suhu ≤ 30 °C</div>
              <div className="legend-item"><span className="dot buruk">●</span> Buruk : suhu ≤ 15 °C atau suhu ≥ 32 °C</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="card-header">
              <span className="card-title"><IoWaterOutline /> Keasaman Air (pH)</span>
              <span className={`status-pill ${data.ph >= 6.5 && data.ph <= 7.0 ? 'baik' : 'buruk'}`}>
                ● {data.ph >= 6.5 && data.ph <= 7.0 ? 'Baik' : 'Buruk'}
              </span>
            </div>
            <div className="metric-main">
              <span className="big-value">{data.ph}</span>
            </div>
            <div className="card-legend">
              <div className="legend-item"><span className="dot baik">●</span> Baik : 6.5 ≤ pH ≤ 7.0</div>
              <div className="legend-item"><span className="dot buruk">●</span> Buruk : pH ≤ 6 atau pH ≥ 7.5</div>
            </div>
          </div>

          <div className="metric-card">
            <div className="card-header">
              <span className="card-title"><BiCubeAlt /> Kualitas Air</span>
              <span className={`status-pill ${data.tds >= 400 && data.tds <= 800 ? 'baik' : 'buruk'}`}>
                ● {data.tds >= 400 && data.tds <= 800 ? 'Baik' : 'Buruk'}
              </span>
            </div>
            <div className="metric-main">
              <span className="big-value">{data.tds}</span>
              <span className="big-unit">ppm</span>
            </div>
            <div className="card-legend">
              <div className="legend-item"><span className="dot baik">●</span> Baik : 400 ≤ ppm ≤ 800</div>
              <div className="legend-item"><span className="dot buruk">●</span> Buruk : ppm ≤ 300 atau ppm ≥ 1500</div>
            </div>
          </div>
        </div>

        {/* ROW 2: GRAFIK & PAKAN */}
        <div className="secondary-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3><IoStatsChartOutline /> Grafik</h3>
              <div className="filter-pill-container">
                {['Hari Ini', '1 Minggu', '1 Bulan', '3 Bulan'].map((range) => (
                  <button 
                    key={range}
                    className={`pill ${activeRange === range ? 'active' : ''}`}
                    onClick={() => setActiveRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
            <div className="chart-area">
              <div className="no-activity-box">
                {chartHistory.labels.length > 0 ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <p>Menunggu data...</p>
                )}
                <div className="chart-labels-right">
                   <div><span style={{color: '#f97316'}}>●</span> Suhu Air</div>
                   <div><span style={{color: '#3b82f6'}}>●</span> pH Air</div>
                   <div><span style={{color: '#a855f7'}}>●</span> Kualitas Air</div>
                </div>
              </div>
              <div className="chart-footer-filters">
                {['Semua', 'Suhu Air', 'pH Air', 'Kualitas Air'].map((cat) => (
                  <button 
                    key={cat}
                    className={`filter-item ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="pakan-card-v4">
            <div className="card-header">
              <span className="card-title"><IoFishOutline /> Level Pakan Ikan</span>
              <span className={`status-pill ${currentPakan.class}`}>● {currentPakan.label}</span>
            </div>
            <div className="pakan-visual-v4">
  <div className="pakan-container-v4">
    {/* Lapisan Warna Isi Ember */}
    <div 
      className="pakan-fill-v4" 
      style={{ 
        height: `${data.pakan}%`,
        backgroundColor: data.pakan > 15 ? '#65A30D' : '#EF4444'
      }}
    />
    {/* Angka Persentase - Sekarang mandiri di luar fill agar tidak hilang */}
    <span>{data.pakan}%</span>
  </div>
</div>
            <div className="pakan-footer-msg">
               <p className={currentPakan.class}>{currentPakan.msg}</p>
            </div>
          </div>
        </div>

        {/* ROW 3: BEBAN */}
        <div className="beban-card-v4">
          <h2><IoFlashOutline /> Beban Real-Time</h2>
          <div className="beban-huge-display">
            {data.beban} <span>Wh</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;